import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";

export type AdminRole = "super_admin" | "admin_support" | null;

/**
 * Resolves the caller's admin role. Either `super_admin` or `admin_support`
 * grants access to the /admin shell; only `super_admin` may take destructive
 * or privileged actions.
 */
export function useAdminSupport() {
  const { user, loading: authLoading } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-role", user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<AdminRole> => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user!.id)
        .in("role", ["super_admin", "admin_support"]);
      if (error || !data?.length) return null;
      if (data.some((r) => r.role === "super_admin")) return "super_admin";
      if (data.some((r) => r.role === "admin_support")) return "admin_support";
      return null;
    },
  });

  const role: AdminRole = data ?? null;
  return {
    role,
    isSuperAdmin: role === "super_admin",
    isSupportAdmin: role === "admin_support",
    isAdminArea: role === "super_admin" || role === "admin_support",
    isLoading: authLoading || (!!user?.id && isLoading),
  };
}
